import type { NextRouter } from 'next/router';
import type { ReactNode } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import type { IntlShape } from 'react-intl';

import { Box } from '@/components/Box';
import { Button } from '@/components/Button';
import { Flex } from '@/components/Flex';
import { Typography } from '@/components/Typography';
import { GOOGLE_RECAPTCHA_PUBLIC_SITE_KEY } from '@/lib/const';
import type { paths } from '@/lib/paths';
import { ORDER_RETURN_QUERY_TYPE } from '@/lib/withdrawal/orderReturnFlow';
import type { WithdrawalCode } from '@/lib/withdrawal/types';
import * as PM from '@/messages/payment';
import type { OrderDetailsGuestSession } from '@/views/OrderDetailsView/helpers';
import { useOrderDetailsContext } from '@/views/OrderDetailsView/OrderDetailsContext';

import { WithdrawalCtaFootnotes } from './WithdrawalCtaFootnotes';

type WithdrawalRouter = {
  paths: typeof paths;
  push: NextRouter['push'];
};

type WithdrawalChildProps = {
  formatMessage: IntlShape['formatMessage'];
  guestEligibilityCaptchaKey: number;
  guestEligibilityFetchFailed: boolean;
  guestSession: OrderDetailsGuestSession | undefined;
  handleRecaptchaChange: (token: string | null) => void;
  handleRecaptchaExpired: () => void;
  isGuestEligibilityRecaptchaVisible: boolean;
  orderId: string;
  orderNotEligibleGlobalMessage: ReactNode;
  router: WithdrawalRouter;
  showGuestReturnButton: boolean;
  showWithdrawalCta: boolean;
  withdrawalBlockedOnlyByOutOfEuScope: boolean;
  withdrawalCode: WithdrawalCode | null;
  withdrawalEligibilityResolvedForUi: boolean;
};

function WithdrawalFootnotes({
  showReturnPolicyFootnote,
  withdrawalCode,
}: Readonly<{
  showReturnPolicyFootnote: boolean;
  withdrawalCode: WithdrawalCode | null;
}>) {
  return (
    <WithdrawalCtaFootnotes
      showReturnPolicyFootnote={showReturnPolicyFootnote}
      withdrawalCode={withdrawalCode}
    />
  );
}

function OutOfEuScopeWithdrawalNotice({
  formatMessage,
}: Readonly<{ formatMessage: IntlShape['formatMessage'] }>) {
  return (
    <Typography
      as='div'
      color='secondaryGrey'
      data-testid='OrderDetailsViewWithdrawalOutOfEuScopeNotice'
      marginBottom={2}
      scale='p3'
      textAlign='center'
    >
      {formatMessage(PM.paymentMessages.withdrawalReturnContactCustomerSupport)}
    </Typography>
  );
}

function GuestWithdrawalContent({
  formatMessage,
  guestEligibilityCaptchaKey,
  guestEligibilityFetchFailed,
  guestSession,
  handleRecaptchaChange,
  handleRecaptchaExpired,
  isGuestEligibilityRecaptchaVisible,
  orderNotEligibleGlobalMessage,
  showGuestReturnButton,
  showWithdrawalCta,
  withdrawalBlockedOnlyByOutOfEuScope,
  withdrawalCode,
  withdrawalEligibilityResolvedForUi,
}: Readonly<
  Pick<
    WithdrawalChildProps,
    | 'formatMessage'
    | 'guestEligibilityCaptchaKey'
    | 'guestEligibilityFetchFailed'
    | 'guestSession'
    | 'handleRecaptchaChange'
    | 'handleRecaptchaExpired'
    | 'isGuestEligibilityRecaptchaVisible'
    | 'orderNotEligibleGlobalMessage'
    | 'showGuestReturnButton'
    | 'showWithdrawalCta'
    | 'withdrawalBlockedOnlyByOutOfEuScope'
    | 'withdrawalCode'
    | 'withdrawalEligibilityResolvedForUi'
  >
>) {
  if (!guestSession) return null;

  const showOutOfEuNotice =
    withdrawalBlockedOnlyByOutOfEuScope && withdrawalEligibilityResolvedForUi;

  return (
    <>
      {orderNotEligibleGlobalMessage}

      {showGuestReturnButton ||
      isGuestEligibilityRecaptchaVisible ||
      showOutOfEuNotice ? (
        <Flex alignItems='center' flexDirection='column' width='100%'>
          {isGuestEligibilityRecaptchaVisible ? (
            <Box marginBottom={2}>
              <ReCAPTCHA
                key={guestEligibilityCaptchaKey}
                onChange={handleRecaptchaChange}
                onExpired={handleRecaptchaExpired}
                sitekey={GOOGLE_RECAPTCHA_PUBLIC_SITE_KEY}
              />
              {guestEligibilityFetchFailed ? (
                <Typography
                  color='error'
                  data-testid='OrderDetailsViewGuestEligibilityFetchError'
                  marginTop={1}
                  scale='p3'
                  textAlign='center'
                >
                  {formatMessage(PM.paymentMessages.withdrawalGenericError)}
                </Typography>
              ) : null}
            </Box>
          ) : null}
          {showGuestReturnButton ? (
            <Button
              data-test-id='OrderDetailsViewStartReturnButton'
              disabled={!showWithdrawalCta}
              onClick={() => {
                guestSession.onStartReturn();
              }}
              type='button'
            >
              {formatMessage(PM.paymentMessages.initiateReturnOrder)}
            </Button>
          ) : null}
          {showOutOfEuNotice ? (
            <OutOfEuScopeWithdrawalNotice formatMessage={formatMessage} />
          ) : null}
        </Flex>
      ) : null}

      {withdrawalEligibilityResolvedForUi ? (
        <WithdrawalFootnotes
          showReturnPolicyFootnote={showWithdrawalCta}
          withdrawalCode={withdrawalCode}
        />
      ) : (
        <WithdrawalFootnotes
          showReturnPolicyFootnote={true}
          withdrawalCode={null}
        />
      )}
    </>
  );
}

function RegisteredWithdrawalContent({
  formatMessage,
  orderId,
  orderNotEligibleGlobalMessage,
  router,
  showWithdrawalCta,
  withdrawalCode,
}: Readonly<
  Pick<
    WithdrawalChildProps,
    | 'formatMessage'
    | 'orderId'
    | 'orderNotEligibleGlobalMessage'
    | 'router'
    | 'showWithdrawalCta'
    | 'withdrawalCode'
  >
>) {
  const { paths, push } = router;

  if (showWithdrawalCta) {
    return (
      <>
        <Flex justifyContent='center' width='100%'>
          <Button
            data-test-id='OrderDetailsViewStartReturnButton'
            onClick={() => {
              push(
                paths.myAccount.myOrders.orderReturn.url({
                  orderId,
                  query: {
                    type: ORDER_RETURN_QUERY_TYPE,
                  },
                }),
              );
            }}
            type='button'
          >
            {formatMessage(PM.paymentMessages.initiateReturnOrder)}
          </Button>
        </Flex>
        <WithdrawalFootnotes
          showReturnPolicyFootnote={true}
          withdrawalCode={withdrawalCode}
        />
      </>
    );
  }

  return (
    <>
      {orderNotEligibleGlobalMessage}
      <WithdrawalFootnotes
        showReturnPolicyFootnote={false}
        withdrawalCode={withdrawalCode}
      />
    </>
  );
}

export function OrderHistoryWithdrawalSection() {
  const ctx = useOrderDetailsContext();
  const { withdrawal } = ctx;

  if (!withdrawal.showOrderHistoryWithdrawalSection) {
    return null;
  }

  const formatMessage = ctx.formatMessage;
  const guestSession = ctx.guestSession;
  const orderId = ctx.order.id;
  const router: WithdrawalRouter = { paths: ctx.paths, push: ctx.push };

  const {
    guestEligibilityCaptchaKey,
    guestEligibilityFetchFailed,
    handleRecaptchaChange,
    handleRecaptchaExpired,
    isGuestEligibilityRecaptchaVisible,
    orderNotEligibleGlobalMessage,
    showGuestReturnButton,
    showWithdrawalCta,
    withdrawalBlockedOnlyByOutOfEuScope,
    withdrawalCode,
    withdrawalEligibilityResolvedForUi,
  } = withdrawal;

  return (
    <Box marginTop={2}>
      {guestSession ? (
        <GuestWithdrawalContent
          formatMessage={formatMessage}
          guestEligibilityCaptchaKey={guestEligibilityCaptchaKey}
          guestEligibilityFetchFailed={guestEligibilityFetchFailed}
          guestSession={guestSession}
          handleRecaptchaChange={handleRecaptchaChange}
          handleRecaptchaExpired={handleRecaptchaExpired}
          isGuestEligibilityRecaptchaVisible={
            isGuestEligibilityRecaptchaVisible
          }
          orderNotEligibleGlobalMessage={orderNotEligibleGlobalMessage}
          showGuestReturnButton={showGuestReturnButton}
          showWithdrawalCta={showWithdrawalCta}
          withdrawalBlockedOnlyByOutOfEuScope={
            withdrawalBlockedOnlyByOutOfEuScope
          }
          withdrawalCode={withdrawalCode}
          withdrawalEligibilityResolvedForUi={
            withdrawalEligibilityResolvedForUi
          }
        />
      ) : (
        <RegisteredWithdrawalContent
          formatMessage={formatMessage}
          orderId={orderId}
          orderNotEligibleGlobalMessage={orderNotEligibleGlobalMessage}
          router={router}
          showWithdrawalCta={showWithdrawalCta}
          withdrawalCode={withdrawalCode}
        />
      )}
    </Box>
  );
}
